import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDate, inr } from "@/lib/format";

const STATUSES = ["draft", "shared", "won", "lost", "converted"];
const COLS = [
  { key: "quote_no", label: "Quotation", render: (q) => <span className="font-mono font-semibold text-sky-800">{q.quote_no}<span className="ml-1 text-[10px] text-slate-500">{q.revision_code}</span></span>, value: (q) => `${q.quote_no}-${q.revision_code}` },
  { key: "quote_date", label: "Date", render: (q) => fmtDate(q.quote_date) },
  { key: "company_name_snapshot", label: "Party", wrap: true, render: (q) => <>{q.company_name_snapshot}{q.party_type === "prospect" && <span className="ml-1 text-[10px] font-semibold uppercase text-violet-700">prospect</span>}</> },
  { key: "project_reference", label: "Project ref" },
  { key: "sales_owner", label: "Sales" },
  { key: "valid_until", label: "Valid until", render: (q) => fmtDate(q.valid_until) },
  { key: "manual_line_count", label: "Lines", right: true, render: (q) => <>{q.line_count}{q.manual_line_count > 0 && <span className="ml-1 text-[10px] text-amber-700">{q.manual_line_count} manual</span>}</> },
  { key: "subtotal", label: "Basic value", right: true, render: (q) => inr(q.subtotal - q.discount_total) },
  { key: "grand_total", label: "Grand total", right: true, render: (q) => inr(q.grand_total) },
  { key: "status", label: "Status", render: (q) => <span className="inline-flex gap-1 items-center"><StatusBadge status={q.status} />{q.converted_to_lm_no && <span className="font-mono text-[11px]">{q.converted_to_lm_no}</span>}</span> },
];

export default function Quotes() {
  const { can } = useMe();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState(sp.get("status") || "all");
  const { data } = useFetch(`/quotes${status === "all" ? "" : `?status=${status}`}`);
  return (
    <div data-testid="quotes-page">
      <PageHeader title="Quotations" subtitle="Latest revision of each quotation. Numbers run per financial year (TQ/26-27/0001); revisions R0, R1… are kept.">
        {can("sales.create") && <Button onClick={() => navigate("/sales/quotes/new")} data-testid="quote-new-btn"><Plus className="h-4 w-4 mr-1" />New quotation</Button>}
      </PageHeader>
      <DataTable testid="quotes-table" columns={COLS} rows={data} onRowClick={(q) => navigate(`/sales/quotes/${q.id}`)} exportName="quotations" exportPerm="sales.export" empty="No quotations yet"
        toolbar={<Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44 bg-white" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>} />
    </div>
  );
}
